import { useState } from "react";
import axios from "axios";
import { X, Pencil, MessageSquare, Calendar, MapPin, FileText, AlertTriangle, Clock, User, DollarSign, CheckCircle2, Mail, Send, FileDown, Lock, Unlock, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/StatusBadge";
import PriorityBadge from "@/components/PriorityBadge";
import EmailStatusBadge from "@/components/EmailStatusBadge";
import AttachmentUploader from "@/components/AttachmentUploader";
import EmailHistory from "@/components/EmailHistory";
import { getOverdueStatus, getTimeRemaining } from "@/utils/overdueUtils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOrderDetails = ({ open, onOpenChange, workOrder, onEdit, onRefresh }) => {
  const { user: currentUser } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [closing, setClosing] = useState(false);
  const [emailHistoryRefreshKey, setEmailHistoryRefreshKey] = useState(0);

  if (!workOrder) return null;

  const role = currentUser?.role;
  const canClose = role === "admin" || role === "manager";
  const canReopen = role === "manager";
  const atts = workOrder.attachments || {};
  const hasCmr = !!atts.corrective_maintenance;
  const hasBefore = !!atts.before;
  const hasAfter = !!atts.after;
  const evidenceComplete = hasCmr && hasBefore && hasAfter;
  const isCompleted = workOrder.status === "Completed";
  const isClosed = !!workOrder.is_closed;

  const formatDateTime = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy 'at' HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setAddingComment(true);
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/comments`, {
        text: newComment,
      });
      toast.success("Comment added successfully");
      setNewComment("");
      onRefresh();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  const handleResendEmail = async () => {
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/resend-email`);
      toast.success(`Email queued to ${workOrder.engineer_email}`);
      setTimeout(() => {
        onRefresh();
        setEmailHistoryRefreshKey((k) => k + 1);
      }, 3000);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to resend email";
      toast.error(msg);
    }
  };

  const handleDownloadPDF = () => {
    const url = `${API}/work-orders/${workOrder.id}/pdf`;
    // Open in new tab; browser will save/display PDF
    window.open(url, "_blank");
    toast.success("PDF downloading...");
  };

  const handleClose = async () => {
    if (!evidenceComplete) {
      toast.error("Upload CMR + before + after photos before closing.");
      return;
    }
    if (!window.confirm("Fully close this work order? Only a manager can reopen it later.")) return;
    setClosing(true);
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/close`);
      toast.success("Work order closed");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close work order");
    } finally {
      setClosing(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm("Reopen this closed work order?")) return;
    setClosing(true);
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/reopen`);
      toast.success("Work order reopened");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reopen work order");
    } finally {
      setClosing(false);
    }
  };

  const formatClosedAt = (iso) => {
    try {
      return format(new Date(iso), "MMM dd, yyyy 'at' HH:mm");
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-[#E4E4E7] rounded-sm max-h-[90vh] overflow-y-auto" data-testid="work-order-details-dialog">
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Manrope' }}>
                {workOrder.work_order_number}
              </DialogTitle>
              <StatusBadge status={workOrder.status} isClosed={workOrder.is_closed} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                className="border-[#E4E4E7] rounded-sm"
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onEdit(workOrder);
                }}
                className="border-[#E4E4E7] rounded-sm"
                data-testid="edit-from-details-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Site Name & Engineer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                <MapPin className="w-4 h-4" />
                Site Name
              </div>
              <p className="text-base text-[#09090B] font-medium">{workOrder.site_name}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                <User className="w-4 h-4" />
                Assigned Engineer
              </div>
              {workOrder.engineer_name ? (
                <div className="space-y-1.5">
                  <p className="text-base text-[#09090B] font-medium flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#F4F4F5] border border-[#E4E4E7] flex items-center justify-center text-sm font-bold text-[#09090B]">
                      {workOrder.engineer_name.charAt(0).toUpperCase()}
                    </span>
                    {workOrder.engineer_name}
                  </p>
                  {workOrder.engineer_email && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-[#71717A] flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {workOrder.engineer_email}
                      </span>
                      <EmailStatusBadge workOrder={workOrder} />
                      {(workOrder.email_status === "failed" || workOrder.email_status === "sent") && (
                        <button
                          onClick={handleResendEmail}
                          className="text-xs text-[#0F172A] hover:text-[#1E293B] underline flex items-center gap-1"
                          data-testid="resend-email-btn"
                        >
                          <Send className="w-3 h-3" />
                          Resend
                        </button>
                      )}
                    </div>
                  )}
                  {workOrder.email_status === "failed" && workOrder.email_error && (
                    <p className="text-xs text-[#DC2626] mt-1" data-testid="email-error-message">
                      Error: {workOrder.email_error}
                    </p>
                  )}
                  {workOrder.email_status === "sent" && workOrder.email_attachments_count > 0 && (
                    <p className="text-xs text-[#059669] mt-1">
                      Email includes {workOrder.email_attachments_count} photo{workOrder.email_attachments_count > 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-base text-[#71717A] italic">Unassigned</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
              <FileText className="w-4 h-4" />
              Description
            </div>
            <p className="text-sm text-[#09090B] leading-relaxed whitespace-pre-wrap">{workOrder.description}</p>
          </div>

          {/* Job Done Remark */}
          {workOrder.job_done_remark && workOrder.job_done_remark.trim() && (
            <div className="space-y-2 bg-[#D1FAE5] border border-[#A7F3D0] rounded-sm p-3" data-testid="job-done-remark-section">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#059669]">
                <CheckCircle2 className="w-4 h-4" />
                Job Done Remark
              </div>
              <p className="text-sm text-[#09090B] leading-relaxed whitespace-pre-wrap">{workOrder.job_done_remark}</p>
            </div>
          )}

          {/* Priority & Billing */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                Priority
              </div>
              <PriorityBadge priority={workOrder.priority} showDesc={true} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                <DollarSign className="w-4 h-4" />
                Billing
              </div>
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-sm text-sm font-bold border ${
                  workOrder.is_chargeable !== false
                    ? "bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]"
                    : "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]"
                }`}
                data-testid="details-billing"
              >
                {workOrder.is_chargeable !== false ? "Chargeable" : "Non-Chargeable"}
              </span>
            </div>
          </div>

          {/* Overdue Warning */}
          {(() => {
            const overdueStatus = getOverdueStatus(workOrder);
            if (overdueStatus === "none") return null;
            const isOverdue = overdueStatus === "overdue";
            return (
              <div
                className={`flex items-center gap-3 rounded-sm p-3 border ${
                  isOverdue
                    ? "bg-[#FEE2E2] border-[#FECACA]"
                    : "bg-[#FFEDD5] border-[#FED7AA]"
                }`}
                data-testid={isOverdue ? "details-overdue-warning" : "details-approaching-warning"}
              >
                {isOverdue ? (
                  <AlertTriangle className="w-5 h-5 text-[#DC2626] flex-shrink-0" />
                ) : (
                  <Clock className="w-5 h-5 text-[#EA580C] flex-shrink-0" />
                )}
                <div>
                  <div
                    className={`text-xs tracking-[0.2em] uppercase font-bold ${
                      isOverdue ? "text-[#DC2626]" : "text-[#EA580C]"
                    }`}
                  >
                    {isOverdue ? "Overdue" : "Due Soon"}
                  </div>
                  <div className="text-sm font-semibold text-[#09090B]">{getTimeRemaining(workOrder)}</div>
                </div>
              </div>
            );
          })()}

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                <Calendar className="w-4 h-4" />
                Start Date & Time
              </div>
              <p className="text-sm text-[#09090B]">{formatDateTime(workOrder.start_datetime)}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
                <Calendar className="w-4 h-4" />
                End Date & Time
              </div>
              <p className="text-sm text-[#09090B]">{formatDateTime(workOrder.end_datetime)}</p>
            </div>
          </div>

          {/* Closure evidence banner + action */}
          {(isCompleted || isClosed) && (
            <div
              className={`rounded-sm border p-4 ${
                isClosed
                  ? "bg-[#D1FAE5] border-[#A7F3D0]"
                  : evidenceComplete
                    ? "bg-[#DBEAFE] border-[#BFDBFE]"
                    : "bg-[#FEF3C7] border-[#FDE68A]"
              }`}
              data-testid="closure-banner"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      isClosed ? "bg-[#059669]" : "bg-[#B45309]"
                    }`}
                  >
                    {isClosed ? (
                      <ShieldCheck className="w-5 h-5 text-white" />
                    ) : (
                      <Lock className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs tracking-[0.2em] uppercase font-bold ${
                        isClosed ? "text-[#059669]" : "text-[#B45309]"
                      }`}
                    >
                      {isClosed ? "Fully Closed" : "Job Done — Awaiting Closure"}
                    </div>
                    <div className="text-sm font-semibold text-[#09090B] mt-0.5">
                      {isClosed ? (
                        <>
                          Closed by <span className="font-bold">@{workOrder.closed_by || "—"}</span>
                          {workOrder.closed_at && (
                            <span className="text-[#71717A] font-normal">
                              {" "}· {formatClosedAt(workOrder.closed_at)}
                            </span>
                          )}
                        </>
                      ) : evidenceComplete ? (
                        "All evidence uploaded — click Close to fully close."
                      ) : (
                        "Upload CMR + before + after to fully close this work order."
                      )}
                    </div>
                    {!isClosed && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid="closure-checklist">
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm border ${
                            hasCmr
                              ? "bg-white text-[#059669] border-[#A7F3D0]"
                              : "bg-white/60 text-[#B45309] border-[#FDE68A]"
                          }`}
                          data-testid="closure-check-cmr"
                        >
                          {hasCmr ? "✓" : "○"} CMR
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm border ${
                            hasBefore
                              ? "bg-white text-[#059669] border-[#A7F3D0]"
                              : "bg-white/60 text-[#B45309] border-[#FDE68A]"
                          }`}
                          data-testid="closure-check-before"
                        >
                          {hasBefore ? "✓" : "○"} Before
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm border ${
                            hasAfter
                              ? "bg-white text-[#059669] border-[#A7F3D0]"
                              : "bg-white/60 text-[#B45309] border-[#FDE68A]"
                          }`}
                          data-testid="closure-check-after"
                        >
                          {hasAfter ? "✓" : "○"} After
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {!isClosed && canClose && (
                  <Button
                    onClick={handleClose}
                    disabled={!evidenceComplete || closing}
                    title={
                      !evidenceComplete
                        ? "Upload CMR + before + after photos first"
                        : undefined
                    }
                    className="bg-[#059669] hover:bg-[#047857] text-white rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="close-work-order-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {closing ? "Closing..." : "Close Work Order"}
                  </Button>
                )}
                {isClosed && canReopen && (
                  <Button
                    onClick={handleReopen}
                    disabled={closing}
                    variant="outline"
                    className="border-[#059669] text-[#059669] hover:bg-[#D1FAE5] rounded-sm"
                    data-testid="reopen-work-order-btn"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    {closing ? "Reopening..." : "Reopen"}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Photo Attachments */}
          <div className="border-t border-[#E4E4E7] pt-6">
            <AttachmentUploader
              workOrderId={workOrder.id}
              attachments={workOrder.attachments}
              onChanged={onRefresh}
            />
          </div>

          {/* Email History */}
          <div className="border-t border-[#E4E4E7] pt-6">
            <EmailHistory workOrderId={workOrder.id} refreshKey={emailHistoryRefreshKey} />
          </div>

          {/* Comments Section */}
          <div className="border-t border-[#E4E4E7] pt-6">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A] mb-4">
              <MessageSquare className="w-4 h-4" />
              Comments ({workOrder.comments?.length || 0})
            </div>

            {/* Existing Comments */}
            {workOrder.comments && workOrder.comments.length > 0 ? (
              <div className="space-y-3 mb-4" data-testid="comments-list">
                {workOrder.comments.map((comment, index) => (
                  <div
                    key={index}
                    className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm p-3"
                    data-testid={`comment-${index}`}
                  >
                    <p className="text-sm text-[#09090B] mb-1">{comment.text}</p>
                    <p className="text-xs text-[#71717A]">{formatDateTime(comment.timestamp)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#71717A] mb-4" data-testid="no-comments">No comments yet</p>
            )}

            {/* Add Comment */}
            <div className="space-y-2">
              <Label htmlFor="new-comment" className="text-sm font-medium text-[#09090B]">
                Add a comment
              </Label>
              <Textarea
                id="new-comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write your comment here..."
                rows={3}
                className="border-[#E4E4E7] rounded-sm resize-none"
                data-testid="comment-input"
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm transition-colors duration-200"
                data-testid="add-comment-btn"
              >
                {addingComment ? "Adding..." : "Add Comment"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderDetails;